import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTimes, FaPlus, FaTrash, FaSearch } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import './LiveListDetails.css';

// Dummy data for "experience" role
const dummyAllProductsForExperience = [ // Products that can be "added"
  { id: 'exp001', handle: 'exp-tshirt-galaxy', title: 'Experience T-Shirt - Galaxy', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Galaxy+Tee' }] },
  { id: 'exp002', handle: 'exp-mug-astronaut', title: 'Experience Mug - Astronaut', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Astro+Mug' }] },
  { id: 'exp003', handle: 'exp-hoodie-nebula', title: 'Experience Hoodie - Nebula', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Nebula+Hoodie' }] },
  { id: 'exp005', handle: 'exp-water-bottle', title: 'Experience Water Bottle - SS', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Steel+Bottle' }] },
  { id: 'exp007', handle: 'exp-backpack-voyager', title: 'Experience Backpack - Voyager', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Voyager+Pack' }] },
  { id: 'exp010', handle: 'exp-phone-case-mars', title: 'Experience Phone Case - Mars', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Mars+Case' }] },
  { id: 'exp013', handle: 'exp-scarf-starry', title: 'Experience Scarf - Starry Night', images: [{ src: 'https://via.placeholder.com/80x80.png?text=Starry+Scarf' }] },
];

const dummyDetailedLiveListData = {
  'exp_live_list_1': {
    listDetails: {
      id: 'exp_live_list_1',
      name: 'Weekend Flash Sale (Experience)',
      description: 'Get our bestsellers at a discounted price this weekend only! Limited stock.',
      scheduled_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      platform: 'Instagram',
      shipping_fee: 5.99,
      status: 'active',
      products: [
        { product_id: 'exp001', reference: 'exp-tshirt-galaxy', price: 25.99, title: 'Experience T-Shirt - Galaxy', image_url: 'https://via.placeholder.com/80x80.png?text=Galaxy+Tee' },
        { product_id: 'exp002', reference: 'exp-mug-astronaut', price: 12.50, title: 'Experience Mug - Astronaut', image_url: 'https://via.placeholder.com/80x80.png?text=Astro+Mug' },
        { product_id: 'exp005', reference: 'exp-water-bottle', price: 20.00, title: 'Experience Water Bottle - SS', image_url: 'https://via.placeholder.com/80x80.png?text=Steel+Bottle' },
      ],
    },
    orders: [
      { id: 'exp_ord_1', draft_order_id: 'DRAFT_EXP001', customer_email: 'customer1@example.com', completed_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), total_amount: 38.49, payment_type: 'stripe', items: [{ title: 'Experience T-Shirt - Galaxy', quantity: 1, price: 25.99, image_url: 'https://via.placeholder.com/50x50.png?text=Galaxy+Tee' }] },
      { id: 'exp_ord_2', draft_order_id: 'DRAFT_EXP002', customer_email: 'customer2@example.com', completed_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), total_amount: 20.00, payment_type: 'click_and_collect', items: [{ title: 'Experience Water Bottle - SS', quantity: 1, price: 20.00, image_url: 'https://via.placeholder.com/50x50.png?text=Steel+Bottle' }] },
    ],
    newCustomers: [
      { id: 'exp_cust_1', name: 'Alice Experience', email: 'alice.exp@example.com', first_purchase_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), total_spent: 38.49 },
    ],
    ordersSummary: { total_orders: 2, average_order_value: 29.25, total_items: 2, online_payment_orders: 1, click_collect_orders: 1 },
    customersSummary: { total_new_customers: 1, total_customers: 2, new_customer_percentage: 50.0, average_first_purchase_value: 38.49 },
  },
  // Add more dummy list details if needed, e.g., for 'exp_live_list_2'
  'exp_live_list_2': {
    listDetails: {
      id: 'exp_live_list_2',
      name: 'New Arrivals Showcase (Experience)',
      description: 'Check out our latest collection of awesome experience gear!',
      scheduled_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      platform: 'TikTok',
      shipping_fee: 0,
      status: 'inactive',
      products: [
        { product_id: 'exp007', reference: 'exp-backpack-voyager', price: 59.99, title: 'Experience Backpack - Voyager', image_url: 'https://via.placeholder.com/80x80.png?text=Voyager+Pack' },
        { product_id: 'exp010', reference: 'exp-phone-case-mars', price: 18.75, title: 'Experience Phone Case - Mars', image_url: 'https://via.placeholder.com/80x80.png?text=Mars+Case' },
      ],
    },
    orders: [],
    newCustomers: [],
    ordersSummary: { total_orders: 0, average_order_value: 0, total_items: 0, online_payment_orders: 0, click_collect_orders: 0 },
    customersSummary: { total_new_customers: 0, total_customers: 0, new_customer_percentage: 0, average_first_purchase_value: 0 },
  },
};

const LiveListDetails = () => {
  const { t } = useTranslation();
  const { listId } = useParams();
  const navigate = useNavigate();
  const [listDetails, setListDetails] = useState(null);
  const [orders, setOrders] = useState([]);
  const [newCustomers, setNewCustomers] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState(null);
  const [customersSummary, setCustomersSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [clickCollectSearch, setClickCollectSearch] = useState('');
  const [completingOrders, setCompletingOrders] = useState(new Set());
  const [orderStatuses, setOrderStatuses] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editableProducts, setEditableProducts] = useState([]);
  const [productPrices, setProductPrices] = useState({});
  const [updatingPrice, setUpdatingPrice] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  const fetchAllProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;
      if (userDataString) userRole = JSON.parse(userDataString).role;

      if (userRole === 'experience') {
        console.log("Experience user: setting dummy allProducts");
        setAllProducts(dummyAllProductsForExperience);
        setIsLoadingProducts(false);
        return;
      }

      const token = sessionStorage.getItem('access_token');
      const response = await axios.get('/api/shopify/get_products', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllProducts(response.data.products || []);
    } catch (err) {
      console.error("Error fetching all products:", err);
      setUpdateError(t('liveListDetails.errors.fetchProductsError'));
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchData = async () => {
    setError(null);
    setUpdateError(null);
    setIsLoading(true);
    console.log(`🔄 Starting fetchData for listId: ${listId}`);
    
    try {
      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;
      if (userDataString) userRole = JSON.parse(userDataString).role;

      if (userRole === 'experience') {
        console.log(`Experience user: fetching dummy details for listId: ${listId}`);
        const dummyData = dummyDetailedLiveListData[listId];
        if (dummyData) {
          setListDetails(dummyData.listDetails);
          setOrders(dummyData.orders);
          setOrdersSummary(dummyData.ordersSummary);
          setNewCustomers(dummyData.newCustomers);
          setCustomersSummary(dummyData.customersSummary);

          const productsWithPrices = dummyData.listDetails.products?.map(p => ({
            _tempId: uuidv4(),
            product_id: p.product_id,
            reference: p.reference,
            price: p.price,
            title: p.title, 
            image_url: p.image_url 
          })) || [];
          setEditableProducts(productsWithPrices);
          
          const pricesMap = {};
          productsWithPrices.forEach(product => {
            if (product.price !== undefined) {
              pricesMap[product._tempId] = product.price;
            }
          });
          setProductPrices(pricesMap);
        } else {
          setError(t('liveListDetails.errors.dummyListNotFound', 'Dummy data for this list ID was not found.'));
        }
        setIsLoading(false);
        return;
      }

      // Real data fetching with individual logging for each endpoint
      console.log('📋 Starting API calls for live list data...');
      
      // Individual API calls with detailed logging
      let listDetailsResponse = null;
      let ordersResponse = null;
      let customersResponse = null;
      
      const startTime = Date.now();
      
      try {
        console.log(`🔍 Fetching list details: /api/shopify/live_products_list/${listId}`);
        const listDetailsStart = Date.now();
        listDetailsResponse = await axios.get(`/api/shopify/live_products_list/${listId}`, { headers });
        const listDetailsEnd = Date.now();
        console.log(`✅ List details fetched successfully in ${listDetailsEnd - listDetailsStart}ms - Products count: ${listDetailsResponse.data.products?.length || 0}`);
      } catch (err) {
        console.error(`❌ List details API failed:`, err);
        console.error(`❌ List details error status: ${err.response?.status}, message: ${err.message}`);
        throw new Error(`List details endpoint failed: ${err.response?.status || 'Unknown'} - ${err.message}`);
      }

      try {
        console.log(`📊 Fetching orders: /api/shopify/live_products_list/${listId}/orders`);
        const ordersStart = Date.now();
        ordersResponse = await axios.get(`/api/shopify/live_products_list/${listId}/orders`, { headers });
        const ordersEnd = Date.now();
        console.log(`✅ Orders fetched successfully in ${ordersEnd - ordersStart}ms - Orders count: ${ordersResponse.data.orders?.length || 0}`);
      } catch (err) {
        console.error(`❌ Orders API failed:`, err);
        console.error(`❌ Orders error status: ${err.response?.status}, message: ${err.message}`);
        throw new Error(`Orders endpoint failed: ${err.response?.status || 'Unknown'} - ${err.message}`);
      }

      try {
        console.log(`👥 Fetching customers: /api/shopify/live_products_list/${listId}/new_customers`);
        const customersStart = Date.now();
        customersResponse = await axios.get(`/api/shopify/live_products_list/${listId}/new_customers`, { headers });
        const customersEnd = Date.now();
        console.log(`✅ Customers fetched successfully in ${customersEnd - customersStart}ms - Customers count: ${customersResponse.data.customers?.length || 0}`);
      } catch (err) {
        console.error(`❌ Customers API failed:`, err);
        console.error(`❌ Customers error status: ${err.response?.status}, message: ${err.message}`);
        throw new Error(`Customers endpoint failed: ${err.response?.status || 'Unknown'} - ${err.message}`);
      }

      const totalTime = Date.now() - startTime;
      console.log(`🎉 All API calls completed successfully in ${totalTime}ms`);

      // Set the data
      setListDetails(listDetailsResponse.data);
      setOrders(ordersResponse.data.orders);
      setOrdersSummary(ordersResponse.data.summary);
      setNewCustomers(customersResponse.data.customers);
      setCustomersSummary(customersResponse.data.summary);

      // Initialize editable products with product_id, reference, price, AND a unique tempId
      const productsWithPrices = listDetailsResponse.data.products?.map((p, index) => ({
        _tempId: uuidv4(),
        product_id: p.product_id,
        reference: p.reference,
        price: p.price
      })) || [];
      
      setEditableProducts(productsWithPrices);
      
      // Initialize product prices state using the _tempId as the key
      const pricesMap = {};
      productsWithPrices.forEach(product => {
        if (product.price !== undefined) {
          pricesMap[product._tempId] = product.price;
        }
      });
      setProductPrices(pricesMap);

      console.log(`✅ fetchData completed successfully for listId: ${listId}`);

    } catch (err) {
      console.error(`💥 fetchData failed for listId: ${listId}`, err);
      console.error('💥 Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        message: err.message,
        url: err.config?.url,
        data: err.response?.data
      });
      setError(err.message || err.response?.data?.detail || t('common.error'));
    } finally {
      setIsLoading(false);
      console.log(`🏁 fetchData finished for listId: ${listId}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, [listId]);

  const handleCompleteOrders = async () => {
    try {
      setIsCompleting(true);
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;
      if (userDataString) userRole = JSON.parse(userDataString).role;

      if (userRole === 'experience') {
        console.log("Experience user: Simulating complete all orders.");
        setCompletionStatus({
          success: true,
          message: t('liveListDetails.completionSuccessSimulated', { completed: orders.length, failed: '' })
        });
        // Optionally, update dummy orders' status here if you track it
        setIsCompleting(false);
        // Simulate refetch by slightly altering a piece of data to show change, or just show message
        // For simplicity, we just show message. A real refetch isn't needed for dummy data.
        return;
      }

      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `/api/shopify/live_products_list/${listId}/complete_orders`,
        {},
        { headers }
      );

      setCompletionStatus({
        success: true,
        message: t('liveListDetails.completionSuccess', {
          completed: response.data.total_completed,
          failed: response.data.total_failed ? t('liveListDetails.failedCount', { count: response.data.total_failed }) : ''
        })
      });

      fetchData();
    } catch (err) {
      setCompletionStatus({
        success: false,
        message: t('liveListDetails.completionError', { error: err.message })
      });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleCompleteSingleOrder = async (orderId) => {
    try {
      setCompletingOrders(prev => new Set([...prev, orderId]));
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;
      if (userDataString) userRole = JSON.parse(userDataString).role;

      if (userRole === 'experience') {
        console.log(`Experience user: Simulating complete single order: ${orderId}`);
        setOrderStatuses(prev => ({
          ...prev,
          [orderId]: { success: true, message: t('liveListDetails.orderCompletionSuccessSimulated') }
        }));
        // Simulate refetch if needed or just update UI
        setCompletingOrders(prev => { const next = new Set(prev); next.delete(orderId); return next; });
        return;
      }
      
      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `/api/shopify/live_products_list/${listId}/complete_order/${orderId}`,
        {},
        { headers }
      );

      setOrderStatuses(prev => ({
        ...prev,
        [orderId]: {
          success: true,
          message: response.data.status === 'completed' 
            ? t('liveListDetails.orderCompletionSuccess')
            : t('liveListDetails.orderAlreadyCompleted')
        }
      }));

      if (response.data.live_completed) {
        fetchData();
      }
    } catch (err) {
      setOrderStatuses(prev => ({
        ...prev,
        [orderId]: {
          success: false,
          message: t('liveListDetails.orderCompletionError', { error: err.message })
        }
      }));
    } finally {
      setCompletingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const toggleEditMode = () => {
    if (!isEditing) {
      fetchAllProducts();
      // Re-initialize editable products and prices when entering edit mode
      // Ensure temp IDs are generated if fetched data doesn't have them (e.g., if listDetails wasn't re-fetched)
      const currentProducts = listDetails?.products || [];
      const productsWithTempIds = currentProducts.map(p => ({
         ...p, // Spread existing product data
         _tempId: uuidv4(), // Assign a new unique temp ID for this edit session
         product_id: p.product_id, // Ensure product_id is present
         reference: p.reference, // Ensure reference is present
         price: p.price // Ensure price is present
      }));

      setEditableProducts(productsWithTempIds);

      const pricesMap = {};
      productsWithTempIds.forEach(product => {
        if (product.price !== undefined) {
          pricesMap[product._tempId] = product.price; // Use _tempId as key
        }
      });
      setProductPrices(pricesMap);

    } else {
      setUpdateError(null);
      setProductSearchQuery('');
    }
    setIsEditing(!isEditing);
  };

  const handlePriceChange = (tempId, newPrice) => {
    setProductPrices(prev => ({
      ...prev,
      [tempId]: newPrice // Update price state using _tempId
    }));
  };

  const handleUpdatePrice = async (tempId) => {
    const productToUpdate = editableProducts.find(p => p._tempId === tempId);
    if (!productToUpdate) {
      console.error("Cannot find product with tempId:", tempId);
      setUpdateError("Internal error: Could not find product to update.");
      return;
    }

    const productId = productToUpdate.product_id; 
    setUpdatingPrice(tempId); 
    setUpdateError(null);
    
    try {
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;
      if (userDataString) userRole = JSON.parse(userDataString).role;
      
      const price = parseFloat(productPrices[tempId]);
      if (isNaN(price) || price < 0) {
        throw new Error(t('liveListDetails.errors.invalidPrice'));
      }

      if (userRole === 'experience') {
        console.log(`Experience user: Simulating update price for product ${productId} (tempId: ${tempId}) to ${price}`);
        setEditableProducts(prev => 
          prev.map(p => p._tempId === tempId ? { ...p, price } : p)
        );
        // productPrices state is already updated by handlePriceChange
        setUpdatingPrice(null);
        return;
      }

      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      // Use the price associated with the tempId from the productPrices state
      const priceToSend = parseFloat(productPrices[tempId]); 
      
      if (isNaN(priceToSend) || priceToSend < 0) {
        throw new Error(t('liveListDetails.errors.invalidPrice'));
      }
      
      // API call still uses the actual product_id
      await axios.patch(
        `/api/shopify/live_products_list/${listId}/products/${productId}/price`,
        { price: priceToSend },
        { headers }
      );
      
      // Update the editable products list, finding the item by tempId
      setEditableProducts(prev => 
        prev.map(p => 
          p._tempId === tempId
            ? { ...p, price } // Update the price in the editableProducts state as well
            : p
        )
      );
      
    } catch (err) {
      console.error('Error updating product price:', err);
      setUpdateError(err.response?.data?.detail || err.message || t('liveListDetails.errors.updatePriceError'));
      // Optionally revert the price in productPrices state if needed
      // setProductPrices(prev => ({...prev, [tempId]: productToUpdate.price })); 
    } finally {
      setUpdatingPrice(null); // Clear update status
    }
  };

  const handleAddProduct = async (productToAdd) => {
    const productPayload = {
      product_id: productToAdd.id.toString(),
      reference: productToAdd.handle || productToAdd.id.toString()
      // Price is not sent on add, it's set via subsequent price update or defaults
    };
    
    setUpdateError(null);

    try {
      const userDataString = sessionStorage.getItem('userData');
      let userRole = null;
      if (userDataString) userRole = JSON.parse(userDataString).role;

      if (userRole === 'experience') {
        console.log(`Experience user: Simulating add product: ${productToAdd.title}`);
        if (editableProducts.some(p => p.product_id === productPayload.product_id)) {
          console.log("Experience: Product ID already in the list locally.");
          setUpdateError(t('liveListDetails.errors.productAlreadyInListSimulated', 'Product already in list (Simulated).'));
          setProductSearchQuery('');
          return;
        }
        const newTempId = uuidv4();
        const newEditableProduct = {
          _tempId: newTempId,
          product_id: productPayload.product_id,
          reference: productPayload.reference,
          price: productToAdd.variants?.[0]?.price || 0, // Default price from dummy data or 0
          title: productToAdd.title, // For display
          image_url: productToAdd.images?.[0]?.src // For display
        };
        setEditableProducts(prev => [...prev, newEditableProduct]);
        setProductPrices(prev => ({ ...prev, [newTempId]: newEditableProduct.price }));
        setProductSearchQuery('');
        return;
      }

      const token = sessionStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // API expects product_id and reference
      const response = await axios.post(
          `/api/shopify/live_products_list/${listId}/products`,
          { products: [productPayload] }, 
          { headers }
      );
      
      const updatedListDetails = response.data; // Get the full updated list details
      if (!updatedListDetails || !Array.isArray(updatedListDetails.products)) {
           console.error("CRITICAL ERROR: Backend response for POST did not contain a valid products array!", response.data);
           setUpdateError("Failed to update product list from server response.");
           return;
      }

      setListDetails(updatedListDetails); // Update the main list details

      // Rebuild editableProducts from the new listDetails, ensuring _tempId
      const newEditableProducts = updatedListDetails.products.map(p => ({
          _tempId: uuidv4(), // Assign new temp IDs
          product_id: p.product_id,
          reference: p.reference,
          price: p.price
      }));
      setEditableProducts(newEditableProducts);

      // Rebuild productPrices state from the new editable products
      const newPricesMap = {};
      newEditableProducts.forEach(p => {
          if (p.price !== undefined) {
              newPricesMap[p._tempId] = p.price;
          }
      });
      setProductPrices(newPricesMap);

      setProductSearchQuery('');

    } catch (err) {
        console.error('Error adding product via POST:', err);
        if (err.response) {
            console.error("Add Product Error response data:", err.response.data);
        }
        const errorMsg = err.response?.data?.detail || err.message || t('liveListDetails.errors.addProductError');
        setUpdateError(errorMsg);
    }
  };

  const handleRemoveProduct = (tempIdToRemove) => {
    const productToRemove = editableProducts.find(p => p._tempId === tempIdToRemove);
    if (!productToRemove) return;

    // For experience user, this local removal is the final step for this action.
    // For real users, an API call would be needed here to persist the removal.
    // The current design seems to handle this through a subsequent save of the whole list, 
    // or by not directly supporting removal via API in this specific action.
    // For now, the local removal is consistent with the user's request.

    console.log(`Removing product instance ${productToRemove.product_id} (tempId: ${tempIdToRemove}) locally.`);
    setEditableProducts(prev => prev.filter(p => p._tempId !== tempIdToRemove));
    
    // Also remove the corresponding price entry from the state
    setProductPrices(prev => {
      const next = { ...prev };
      delete next[tempIdToRemove];
      return next;
    });
    
    // NOTE: This only removes locally. Need a save/API call mechanism 
    // if removal should persist without adding/updating prices.
    // The current PATCH for price updates doesn't remove products. 
    // You might need a separate PUT endpoint or similar to save the entire list state.
  };

  const standardOrders = orders.filter(order => order.payment_type === 'stripe');
  const clickCollectOrders = orders.filter(order => order.payment_type === 'click_and_collect');

  const filteredClickCollectOrders = clickCollectOrders.filter(order => 
    order.draft_order_id.toString().includes(clickCollectSearch.toLowerCase()) ||
    order.customer_email.toLowerCase().includes(clickCollectSearch.toLowerCase())
  );

  const filteredAvailableProducts = allProducts.filter(product =>
    !editableProducts.some(p => p.product_id === product.id.toString()) &&
    product.title.toLowerCase().includes(productSearchQuery.toLowerCase())
  );

  const selectedProductDetails = editableProducts.map(selectedProd => {
    // Find full details using product_id as before
    const fullProduct = allProducts.find(p => p.id?.toString() === selectedProd.product_id?.toString());
    
    if (fullProduct) {
      return {
        ...fullProduct,
        product_id: selectedProd.product_id, // Keep original product_id
        _tempId: selectedProd._tempId // Carry over the tempId
      };
    } else {
      // Basic representation, include _tempId
      return {
        id: selectedProd.product_id, // Keep original ID for potential fallback rendering
        product_id: selectedProd.product_id,
        _tempId: selectedProd._tempId, // Carry over the tempId
        title: selectedProd.reference || "Loading product...",
        images: [],
        price: selectedProd.price // Include price if available
      };
    }
  }).filter(Boolean);

  if (!isLoading && (!listDetails || !ordersSummary || !customersSummary)) {
    console.error("Critical data missing after load:", { listDetails, ordersSummary, customersSummary });
    return <div className="error-message">{t('liveListDetails.loadError')}</div>;
  }

  if (error) return <div className="error-message">{t('common.error')}: {error}</div>;
  if (isLoading && !listDetails) return <div className="loading-message">{t('common.loading')}</div>;

  return (
    <div className="live-session-container">
      <header className="live-session-header">
        <h1 className="live-session-title">
          {listDetails?.name || t('liveListDetails.loadingTitle')}
        </h1>
        <button onClick={toggleEditMode} className={`edit-list-button ${isEditing ? 'editing' : ''}`}>
          {isEditing ? <><FaTimes /> {t('common.done')}</> : <><FaEdit /> {t('common.edit')}</>}
        </button>
        {updateError && <div className="error-message update-error">{updateError}</div>}
      </header>

      <section className="list-metadata-section">
        {listDetails?.description && <p className="list-description">{listDetails.description}</p>}
        <div className="list-details-display">
          {listDetails?.scheduled_date && <span>{t('liveListDetails.scheduled')}: {new Date(listDetails.scheduled_date).toLocaleString()}</span>}
          {listDetails?.platform && <span>{t('liveListDetails.platform')}: {listDetails.platform}</span>}
          <span>{t('liveListDetails.shippingFee')}: €{listDetails?.shipping_fee?.toFixed(2) || '0.00'}</span>
          <span>{t('liveListDetails.status')}: {listDetails?.status || 'draft'}</span>
        </div>
      </section>

      {isEditing && (
        <section className="list-metadata-section product-editing-active">
          <div className="list-metadata-edit">
            <div className="form-group product-edit-section">
              <label>{t('liveListDetails.edit.products')}</label>
              <div className="selected-products-list">
                {isLoadingProducts ? (
                  <p>{t('common.loading')}</p>
                ) : selectedProductDetails.length > 0 ? (
                  selectedProductDetails.map(product => (
                    // Use the temporary unique ID (_tempId) as the key for React rendering
                    product && (
                      <div key={product._tempId} className="selected-product-item">
                        <img
                           src={product.images?.[0]?.src || '/placeholder-image.png'}
                           alt={product.title || 'Product'}
                           className="selected-product-img"
                        />
                        <span>{product.title || 'Unknown Product'}</span>
                        <div className="product-price-controls">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            // Read value from productPrices using the unique _tempId
                            value={productPrices[product._tempId] || ''}
                            // Pass the unique _tempId to the change handler
                            onChange={(e) => handlePriceChange(product._tempId, e.target.value)}
                            className="price-input"
                            placeholder={t('liveListDetails.edit.customPrice')}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Pass the unique _tempId to the update handler
                              handleUpdatePrice(product._tempId);
                            }}
                            className="update-price-btn"
                            // Disable based on the _tempId being updated
                            disabled={updatingPrice === product._tempId}
                          >
                            {/* Show updating status based on _tempId */}
                            {updatingPrice === product._tempId ? t('common.updating') : t('common.save')}
                          </button>
                        </div>
                        <button
                           // Pass the unique _tempId to the remove handler
                          onClick={() => handleRemoveProduct(product._tempId)}
                          className="remove-product-btn"
                          title={t('liveListDetails.edit.removeProductWarning')}
                          aria-label={t('liveListDetails.edit.removeProduct')}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )
                  ))
                ) : (
                  <p>{t('liveListDetails.edit.noProductsSelected')}</p>
                )}
              </div>
              <div className="add-product-section">
                <div className="search-product-input-wrapper">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder={t('liveListDetails.edit.searchProductsToAdd')}
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="edit-list-input search-product"
                    disabled={isLoadingProducts}
                  />
                </div>
                {isLoadingProducts && <p>{t('common.loading')}</p>}
                {productSearchQuery && !isLoadingProducts && (
                  <div className="available-products-list">
                    {filteredAvailableProducts.length > 0 ? (
                      filteredAvailableProducts.slice(0, 10).map(product => (
                        product && (
                          <div key={product.id} className="available-product-item" onClick={() => handleAddProduct(product)}>
                            <img
                              src={product.images?.[0]?.src || '/placeholder-image.png'}
                              alt={product.title || 'Product'}
                              className="available-product-img"
                            />
                            <span>{product.title || 'Unknown Product'}</span>
                            <button className="add-product-btn" aria-label={t('liveListDetails.edit.addProduct')}>
                              <FaPlus />
                            </button>
                          </div>
                        )
                      ))
                    ) : (
                      <p>{t('liveListDetails.edit.noProductsFound')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {ordersSummary && customersSummary && (
        <section className="session-summary">
          <div className="summary-item">
            <h3>{t('liveListDetails.totalOrders')}</h3>
            <p>{ordersSummary.total_orders}</p>
          </div>
          <div className="summary-item">
            <h3>{t('liveListDetails.averageOrderValue')}</h3>
            <p>€{ordersSummary.average_order_value.toFixed(2)}</p>
          </div>
          <div className="summary-item">
            <h3>{t('liveListDetails.totalItemsSold')}</h3>
            <p>{ordersSummary.total_items}</p>
          </div>
          <div className="summary-item">
            <h3>{t('liveListDetails.newCustomers')}</h3>
            <p>{customersSummary.total_new_customers}</p>
          </div>
          <div className="summary-item">
            <h3>{t('liveListDetails.totalCustomers')}</h3>
            <p>{customersSummary.total_customers}</p>
          </div>
          <div className="summary-item">
            <h3>{t('liveListDetails.newCustomerRate')}</h3>
            <p>{customersSummary.new_customer_percentage.toFixed(1)}%</p>
          </div>
          <div className="summary-item">
            <h3>{t('liveListDetails.averageFirstPurchase')}</h3>
            <p>€{customersSummary.average_first_purchase_value.toFixed(2)}</p>
          </div>
        </section>
      )}

      {ordersSummary && customersSummary && newCustomers && (
        <div className="live-list-grid-container">
          <section className="live-list-grid-column">
            <div className="standard-orders-header">
              <h2>{t('liveListDetails.standardOrders', { count: ordersSummary.online_payment_orders })}</h2>
              <div className="header-actions">
                <button 
                  className="complete-orders-btn"
                  onClick={handleCompleteOrders}
                  disabled={isCompleting}
                >
                  {isCompleting ? t('liveListDetails.completing') : t('liveListDetails.completeAll')}
                </button>
                {completionStatus && (
                  <div className={`completion-status ${completionStatus.success ? 'success' : 'error'}`}>
                    {completionStatus.message}
                  </div>
                )}
              </div>
            </div>
            <div className="orders-scroll-container">
              {standardOrders.map(order => (
                <div 
                  key={order.id} 
                  className="order-card"
                  onClick={() => handleOrderClick(order.id)}
                >
                  <div className="order-header">
                    <span className="order-id">{t('liveListDetails.orderNumber', { id: order.draft_order_id })}</span>
                    <span className="order-total">€{order.total_amount.toFixed(2)}</span>
                  </div>
                  <div className="order-info">
                    <p>{t('liveListDetails.customer')}: {order.customer_email}</p>
                    <p>{t('liveListDetails.date')}: {new Date(order.completed_at).toLocaleString()}</p>
                    <p>{t('liveListDetails.items')}: {order.items.length}</p>
                  </div>
                  <div className="order-items">
                    {order.items.map((item, index) => (
                      <div key={index} className="order-item">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} className="item-image" />
                        )}
                        <div className="item-details">
                          <p>{item.title}</p>
                          <p>{t('liveListDetails.quantity')}: {item.quantity} × €{item.price.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="live-list-grid-column">
            <div className="click-collect-header">
              <h2>{t('liveListDetails.clickCollectOrders', { count: ordersSummary.click_collect_orders })}</h2>
              <div className="search-container">
                <input
                  type="text"
                  value={clickCollectSearch}
                  onChange={(e) => setClickCollectSearch(e.target.value)}
                  placeholder={t('liveListDetails.searchOrdersPlaceholder')}
                  className="search-input"
                />
              </div>
            </div>
            <div className="orders-scroll-container">
              {filteredClickCollectOrders.map(order => (
                <div 
                  key={order.id} 
                  className="order-card"
                >
                  <div className="order-header">
                    <span className="order-id">{t('liveListDetails.orderNumber', { id: order.draft_order_id })}</span>
                    <span className="order-total">€{order.total_amount.toFixed(2)}</span>
                  </div>
                  <div className="order-info">
                    <p>{t('liveListDetails.customer')}: {order.customer_email}</p>
                    <p>{t('liveListDetails.date')}: {new Date(order.completed_at).toLocaleString()}</p>
                    <p>{t('liveListDetails.items')}: {order.items.length}</p>
                  </div>
                  <div className="order-items">
                    {order.items.map((item, index) => (
                      <div key={index} className="order-item">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.title} className="item-image" />
                        )}
                        <div className="item-details">
                          <p>{item.title}</p>
                          <p>{t('liveListDetails.quantity')}: {item.quantity} × €{item.price.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="order-actions">
                    <button 
                      className={`complete-order-btn ${completingOrders.has(order.draft_order_id) ? 'completing' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCompleteSingleOrder(order.draft_order_id);
                      }}
                      disabled={completingOrders.has(order.draft_order_id)}
                    >
                      {completingOrders.has(order.draft_order_id) 
                        ? t('liveListDetails.completing') 
                        : t('liveListDetails.complete')}
                    </button>
                    {orderStatuses[order.draft_order_id] && (
                      <div className={`order-status ${orderStatuses[order.draft_order_id].success ? 'success' : 'error'}`}>
                        {orderStatuses[order.draft_order_id].message}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="live-list-grid-column">
            <h2>{t('liveListDetails.newCustomers', { count: customersSummary.total_new_customers })}</h2>
            <div className="customers-scroll-container">
              {newCustomers.map(customer => (
                <div 
                  key={customer.email} 
                  className="customer-card"
                  onClick={() => handleCustomerClick(customer.id)}
                >
                  <h3>{customer.name}</h3>
                  <p>{t('liveListDetails.email')}: {customer.email}</p>
                  <p>{t('liveListDetails.firstPurchase')}: {new Date(customer.first_purchase_date).toLocaleString()}</p>
                  <p>{t('liveListDetails.totalSpent')}: €{customer.total_spent.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default LiveListDetails;
